import { localizeValue, scaledRemCalc, sum } from '@mnd-frontend/utils';
import { pie } from 'd3-shape';
import type { FC } from 'react';
import { PositionRelative, SvgChart } from '../lib/components';
import { Arc } from '../lib/components/Shapes';
import { useEventContext, useInteractionContext, useSvgContext } from '../lib/context';

type DataPoint = { id: string; value: number; color: string; index?: number };

type DonutProps = {
  data: DataPoint[];
  unit: string;
  hideSummary: boolean;
  onArcClick?: (index: number) => void;
};

const Donut: FC<DonutProps> = ({ data, unit = '', hideSummary, onArcClick }) => {
  const { width, height } = useSvgContext();
  const { isHovered, isSelected } = useInteractionContext();
  const { handleMouseEnter, handleMouseLeave, handleClick } = useEventContext();

  const arcs = pie<DataPoint>()
    .value(d => d.value)
    .sortValues(null)(data);
  const active = arcs.filter(arc => isSelected(arc.data.id) || isHovered(arc.data.id));
  const radius = Math.min(width / 2, height / 2);

  return (
    <PositionRelative>
      <SvgChart>
        <g transform={`translate(${width / 2} ${height / 2})`}>
          {active.map(({ startAngle, endAngle, data }, i) => (
            <Arc
              key={`selected_${i}`}
              fill={data.color}
              innerRadius={radius * 0.94}
              outerRadius={radius}
              startAngle={startAngle}
              endAngle={endAngle}
              opacity={0.4}
              $clickable
              onClick={() => {
                if (onArcClick && data.index != null) onArcClick(data.index);
              }}
            />
          ))}
          {arcs.map(({ startAngle, endAngle, data }, i) => (
            <Arc
              key={`arc_${i}`}
              fill={data.color}
              innerRadius={radius * 0.64}
              outerRadius={radius - 6}
              startAngle={startAngle}
              endAngle={endAngle}
              onMouseEnter={(event: MouseEvent) => {
                (!hideSummary || onArcClick) && handleMouseEnter(data.id, event);
              }}
              onMouseLeave={(event: MouseEvent) => {
                (!hideSummary || onArcClick) && handleMouseLeave(data.id, event);
              }}
              onClick={(event: MouseEvent) => {
                !hideSummary && handleClick(data.id, event);
                onArcClick && data.index != null && onArcClick(data.index);
              }}
              $clickable={!hideSummary}
            />
          ))}
          {!hideSummary && (
            <>
              <text textAnchor="middle" fontWeight={800} fontSize={scaledRemCalc(32)}>
                {localizeValue(
                  sum(active.length ? active : data, d => d.value),
                  { abbrev: true },
                )}
              </text>
              <text textAnchor="middle" y={radius / 4} fontSize={scaledRemCalc(14)}>
                {unit}
              </text>
            </>
          )}
        </g>
      </SvgChart>
    </PositionRelative>
  );
};

export default Donut;
